import React from 'react';
import { NavigateFunction, NavigationType, useLocation, useNavigate, useNavigationType, useParams } from 'react-router';
import H from 'history';
import classnames from 'classnames';
import { usePrevious } from '@ans/hooks';

export type Location<S = H.LocationState> = H.Location<S>;

export interface RouteComponentProps<Params extends { [K in keyof Params]?: string } = object, S = H.LocationState> {
    location: Location<S>;
    navigate: NavigateFunction;
    params: Params;
}

export interface LastLocationRouteComponentProps<
    Params extends { [K in keyof Params]?: string } = object,
    S = H.LocationState,
> {
    history: H.History;
    location: Location<S>;
    navigate: NavigateFunction;
    params: Params;
    lastLocation: Location;
    navType: NavigationType;
}

export type activeClassFn = ({
    activeClass,
    pendingClass,
    otherClasess,
}: {
    activeClass?: string;
    pendingClass?: string;
    otherClasess?: string[];
}) => ({ isActive, isPending }: { isActive: boolean; isPending: boolean }) => string | undefined;

// TODO ANS-12757 - typing for withRouter and MatchPath
export function withRouter(Component: any) {
    function ComponentWithRouterProp(props: any) {
        const location = useLocation();
        const navigate = useNavigate();
        const params = useParams();

        return <Component {...props} location={location} navigate={navigate} params={params} />;
    }

    return ComponentWithRouterProp;
}

export function lastLocationWithRouter(Component: any) {
    function ComponentWithRouterProp(props: any) {
        const location = useLocation();
        const navigate = useNavigate();
        const params = useParams();
        const navType = useNavigationType();
        const lastLocation = usePrevious(location);

        return (
            <Component
                {...props}
                location={location}
                navigate={navigate}
                params={params}
                navType={navType}
                lastLocation={lastLocation}
            />
        );
    }

    return ComponentWithRouterProp;
}

export const activeClass: activeClassFn =
    ({ activeClass, pendingClass, otherClasess = [] }) =>
    ({ isActive, isPending }) => {
        return classnames(...otherClasess, {
            [activeClass || '']: activeClass && isActive,
            [pendingClass || '']: pendingClass && isPending,
        });
    };
